import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { COLLECTIONS } from '../../firebase/collections';
import { useAuth } from '../../context/AuthContext';
import { useToast } from './Toast';
import Modal from './Modal';
import { checkClientCooldown, markClientCooldown } from '../../utils/clientRateLimit';
import { Bug, AlertCircle, Lightbulb, HelpCircle } from 'lucide-react';
import './FeedbackModal.css';

const TYPE_VALUES = ['bug', 'issue', 'idea', 'other'];
const TYPE_ICONS = { bug: Bug, issue: AlertCircle, idea: Lightbulb, other: HelpCircle };
const PRIORITY_VALUES = ['low', 'medium', 'high'];

const FEEDBACK_COOLDOWN_MS = 60_000;
const DESCRIPTION_MAX = 5000;

export default function FeedbackModal({ onClose }) {
  const { t } = useTranslation();
  const { user, profile, companyId } = useAuth();
  const location = useLocation();
  const toast = useToast();

  const [type, setType]               = useState('bug');
  const [title, setTitle]             = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority]       = useState('medium');
  const [errors, setErrors]           = useState({});
  const [saving, setSaving]           = useState(false);

  const validate = () => {
    const e = {};
    if (!title.trim())       e.title       = t('validation.required');
    if (!description.trim()) e.description = t('validation.required');
    if (description.length > DESCRIPTION_MAX) e.description = t('feedback.descriptionTooLong');
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    const cooldown = checkClientCooldown(`feedback:${user.uid}`, FEEDBACK_COOLDOWN_MS);
    if (!cooldown.allowed) {
      toast(t('feedback.rateLimit'), 'error');
      return;
    }
    setSaving(true);
    try {
      await addDoc(collection(db, COLLECTIONS.FEEDBACK), {
        companyId,
        type,
        title:           title.trim(),
        description:     description.trim(),
        priority,
        status:          'new',
        page:            location.pathname,
        submittedBy:     user.uid,
        submittedByName: profile?.displayName ?? '',
        submittedByRole: profile?.role ?? '',
        userAgent:       navigator.userAgent,
        adminNotes:      '',
        createdAt:       serverTimestamp(),
        updatedAt:       serverTimestamp(),
      });
      markClientCooldown(`feedback:${user.uid}`);
      toast(t('feedback.submitted'), 'success');
      onClose();
    } catch {
      toast(t('feedback.submitFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={t('feedback.shareFeedback')}
      onClose={onClose}
      size="md"
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
            {saving ? <span className="spinner" /> : t('common.submit')}
          </button>
        </>
      }
    >
      <div className="form-group">
        <label className="form-label">{t('feedback.type')}</label>
        <div className="feedback-type-pills">
          {TYPE_VALUES.map((value) => {
            const Icon = TYPE_ICONS[value];
            return (
              <button
                key={value}
                type="button"
                className={`feedback-type-pill ${type === value ? 'active' : ''}`}
                onClick={() => setType(value)}
              >
                <Icon size={13} />
                {t(`feedback.types.${value}`)}
              </button>
            );
          })}
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">{t('feedback.title')} <span className="required">*</span></label>
        <input
          className={`form-control ${errors.title ? 'is-error' : ''}`}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t('feedback.titlePlaceholder')}
          maxLength={120}
          autoFocus
        />
        {errors.title && <p className="form-error">{errors.title}</p>}
      </div>

      <div className="form-group">
        <label className="form-label">{t('feedback.description')} <span className="required">*</span></label>
        <textarea
          className={`form-control ${errors.description ? 'is-error' : ''}`}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('feedback.descriptionPlaceholder')}
          rows={4}
          maxLength={DESCRIPTION_MAX}
          style={{ resize: 'vertical' }}
        />
        {errors.description && <p className="form-error">{errors.description}</p>}
      </div>

      <div className="form-group">
        <label className="form-label">{t('feedback.priority')}</label>
        <div className="feedback-priority-pills">
          {PRIORITY_VALUES.map((value) => (
            <button
              key={value}
              type="button"
              className={`feedback-priority-pill feedback-priority-${value} ${priority === value ? 'active' : ''}`}
              onClick={() => setPriority(value)}
            >
              {t(`feedback.priorities.${value}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">{t('feedback.page')}</label>
        <input
          className="form-control"
          value={location.pathname}
          readOnly
          style={{ color: 'var(--text-muted)', background: 'var(--surface-2)', cursor: 'default' }}
        />
        <p className="form-hint">{t('feedback.pageHint')}</p>
      </div>
    </Modal>
  );
}
